import React, { useMemo, useState, useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAppStore } from "../../core/store/store";
import {
  DASHBOARD_MENU,
  filterMenuByPermissions,
} from "../../core/menu/dashboard.menu";
import { DesktopGrid } from "../widgets/desktop/DesktopGrid";
import { LayoutGrid, Sun, Moon, User } from "lucide-react";

type MenuItem = {
  label: string;
  route?: string;
  action?: () => void;
};

type MenuGroup = {
  label: string;
  items: MenuItem[];
};

const TOPBAR_HEIGHT = 84;
const ICON_SIZE = 18;
const BUTTON_HEIGHT = 38;

export const MacDashboardLayout = () => {
  const nav = useNavigate();
  const loc = useLocation();

  const auth = useAppStore((s) => s.auth);
  const logout = useAppStore((s) => s.authActions.logout);
  const openApp = useAppStore((s) => s.desktopActions.openApp);

  const theme = useAppStore((s) => s.ui.theme);
  const setTheme = useAppStore((s) => s.uiActions.setTheme);

  const [pageMenus, setPageMenus] = useState<MenuGroup[]>([]);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [hoverMenu, setHoverMenu] = useState<string | null>(null);
  const [hoverItem, setHoverItem] = useState<string | null>(null);
  const [hoverIcon, setHoverIcon] = useState<string | null>(null);

  useEffect(() => {
    const close = () => setOpenMenu(null);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, []);

  const menu = useMemo(
    () => filterMenuByPermissions(DASHBOARD_MENU, auth.permissions || []),
    [auth.permissions]
  );

  const isDesktopRoot = loc.pathname === "/dashboard";
  const isDark = theme === "dark";

  const C = isDark
    ? {
        shell: "#0b1220",
        text: "#ffffff",
        textMuted: "rgba(255,255,255,0.55)",
        divider: "rgba(255,255,255,0.08)",
        topbar:
          "linear-gradient(180deg, rgba(20,25,40,0.92), rgba(10,15,30,0.88))",
        border: "rgba(255,255,255,0.14)",
        chip: "rgba(255,255,255,0.06)",
        menuHover: "rgba(255,255,255,0.08)",
        menuActive: "rgba(255,255,255,0.12)",
      }
    : {
        shell: "#f6f7fb",
        text: "#1f2937",
        textMuted: "rgba(31,41,55,0.45)",
        divider: "rgba(0,0,0,0.06)",
        topbar:
          "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(245,247,251,0.94))",
        border: "rgba(0,0,0,0.06)",
        chip: "rgba(0,0,0,0.04)",
        menuHover: "rgba(0,0,0,0.06)",
        menuActive: "rgba(0,0,0,0.08)",
      };

  const iconButtonStyle: React.CSSProperties = {
    height: BUTTON_HEIGHT,
    minWidth: BUTTON_HEIGHT,
    padding: "0 12px",
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    gap: 8,
    cursor: "pointer",
    userSelect: "none",
    backdropFilter: "blur(14px)",
    WebkitBackdropFilter: "blur(14px)",
    background: C.chip,
    border: `1px solid ${C.border}`,
  };

  const breadcrumbs = useMemo(() => {
    const parts = loc.pathname.split("/").filter(Boolean);
    return parts.map((p, i) => ({
      label: p,
      path: "/" + parts.slice(0, i + 1).join("/"),
    }));
  }, [loc.pathname]);

  return (
    <div style={{ minHeight: "100vh", background: C.shell, color: C.text }}>
      {/* TOPBAR */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: TOPBAR_HEIGHT,
          display: "grid",
          gridTemplateColumns: "auto 1fr auto",
          alignItems: "center",
          padding: "0 16px",
          backdropFilter: "blur(14px)",
          background: C.topbar,
          borderBottom: `1px solid ${C.divider}`,
          zIndex: 10000,
        }}
      >
        {/* LEFT */}
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <img
            src="/img/minLogo.png"
            width="44"
            height="44"
            style={{ borderRadius: 50 }}
          />

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              lineHeight: 1.1,
            }}
          >
            <div
              style={{ fontWeight: 700, fontSize: 14, cursor: "pointer" }}
              onClick={() => nav("/dashboard")}
            >
              FixerAviation
            </div>

            <div
              style={{
                display: "flex",
                gap: 6,
                fontSize: 11,
                opacity: 0.75,
                marginTop: 11,
              }}
            >
              {breadcrumbs.map((b, i) => (
                <React.Fragment key={b.path}>
                  <span
                    style={{ cursor: "pointer" }}
                    onClick={() => nav(b.path)}
                  >
                    {b.label}
                  </span>
                  {i < breadcrumbs.length - 1 && <span>/</span>}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        {/* CENTER — OFFICE STYLE MENU */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            gap: 2,
            marginLeft: 20,
          }}
        >
          {pageMenus.map((m) => {
            const active = openMenu === m.label;
            const hovered = hoverMenu === m.label;

            return (
              <div
                key={m.label}
                style={{ position: "relative" }}
                onMouseEnter={() => setHoverMenu(m.label)}
                onMouseLeave={() => setHoverMenu(null)}
              >
                <div
                  style={{
                    padding: "10px 14px 8px",
                    fontSize: 13,
                    cursor: "default",
                    background: active
                      ? C.menuActive
                      : hovered
                      ? C.menuHover
                      : "transparent",
                    borderBottom: `2px solid ${
                      active ? C.text : "transparent"
                    }`,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenMenu((o) => (o === m.label ? null : m.label));
                  }}
                >
                  {m.label}
                </div>

                {active && (
                  <div
                    style={{
                      position: "absolute",
                      top: 42,
                      left: 0,
                      minWidth: 220,
                      background: C.shell,
                      border: `1px solid ${C.border}`,
                      borderRadius: 12,
                      padding: 6,
                      zIndex: 10001,
                    }}
                  >
                    {m.items.map((i) => (
                      <div
                        key={i.label}
                        onMouseEnter={() => setHoverItem(i.label)}
                        onMouseLeave={() => setHoverItem(null)}
                        style={{
                          padding: "8px 12px",
                          borderRadius: 8,
                          fontSize: 13,
                          cursor: "pointer",
                          background:
                            hoverItem === i.label ? C.menuHover : "transparent",
                        }}
                        onClick={() => {
                          setOpenMenu(null);
                          if (i.route) nav(i.route);
                          if (i.action) i.action();
                        }}
                      >
                        {i.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* RIGHT */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div
            style={iconButtonStyle}
            onMouseEnter={() => setHoverIcon("desktop")}
            onMouseLeave={() => setHoverIcon(null)}
            onClick={() => nav("/dashboard")}
          >
            <LayoutGrid
              size={ICON_SIZE}
              color={hoverIcon === "desktop" ? C.text : C.textMuted}
            />
          </div>

          {/* <div
            style={iconButtonStyle}
            onMouseEnter={() => setHoverIcon('theme')}
            onMouseLeave={() => setHoverIcon(null)}
            onClick={() => setTheme(isDark ? 'light' : 'dark')}
          >
            {isDark ? (
              <Moon size={ICON_SIZE} color={hoverIcon === 'theme' ? C.text : C.textMuted} />
            ) : (
              <Sun size={ICON_SIZE} color={hoverIcon === 'theme' ? C.text : C.textMuted} />
            )}
          </div> */}

          <div
            style={iconButtonStyle}
            onMouseEnter={() => setHoverIcon("user")}
            onMouseLeave={() => setHoverIcon(null)}
            onClick={() => {
              logout();
              nav("/");
            }}
          >
            <User
              size={ICON_SIZE}
              color={hoverIcon === "user" ? C.text : C.textMuted}
            />
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                lineHeight: 1.05,
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 600 }}>
                {auth.user?.name}
              </div>
              <div style={{ fontSize: 10, opacity: 0.7 }}>Logout</div>
            </div>
          </div>
        </div>
      </div>

      {/* CONTENT */}
      <div
        style={{
          position: "absolute",
          top: TOPBAR_HEIGHT,
          left: 0,
          right: 0,
          bottom: 0,
        }}
      >
        {isDesktopRoot && (
          <DesktopGrid
            items={menu}
            onOpen={(item) => {
              openApp(item.key);
              nav(item.route);
            }}
          />
        )}
        <Outlet context={{ setPageMenus }} />
      </div>
    </div>
  );
};
